import {Injectable} from '@angular/core';
import {from, Observable, of, throwError} from 'rxjs';
import {concatMap, map, tap, toArray} from 'rxjs/operators';

import {DtoDetail} from 'features/core/shared/dto/dto.detail';
import {DtoImportObjectContext} from 'features/core/shared/dto/dto.import.object.context';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';

import {ProvisionContractDto} from '../../model/dto/entity/provision-contract.dto';
import {ProvisionContractAgentDto} from '../../model/dto/entity/provision-contract-agent.dto';
import {ProvisionEligibilityResult} from '../../enum/provision-eligibility-result.enum';
import {CP_PROVISION_PENDING_BLOCK_TEXT, CP_PROVISION_PENDING_FIELD_TEXT} from './cp-provision-pending-text';

/**
 * State container of the two top-down provision wizards — the create wizard
 * (`cp-provision-contract-create-wizard`) and the adjust wizard
 * (`cp-provision-contract-adjust-wizard`).
 *
 * <h2>Why this exists as a service and not as component fields</h2>
 * Both wizards write the SAME two entities in the SAME order (Provisionszeile first, Provisionssatz
 * second) and therefore need the same four guarantees. Holding those in one place means the
 * guarantees are implemented once and cannot drift apart between the two flows:
 *
 *   1. NOTHING is written before {@link runContractFirst} starts (GK13). Every step of both wizards
 *      captures into a DRAFT held here; abandoning a wizard leaves no ProvisionContract behind.
 *   2. SINGLE FLIGHT — {@link saveBusy} is raised BEFORE the sequence starts, not after the first
 *      response returns, so a second click cannot start a second sequence.
 *   3. RESUME POINTS — {@link createdContract} and {@link CpProvisionContractAgentDraft.createdDto}
 *      make every write of the sequence a no-op once it has succeeded. A retry after a failure on
 *      the child write reuses the parent instead of creating a second one.
 *   4. The backend refusals are SURFACED, never re-implemented. See {@link resolveErrorMessage}.
 *
 * <h2>Instance scope</h2>
 * Declared `@Injectable()` WITHOUT `providedIn`: it carries the state of ONE wizard run and is
 * listed in the `providers` of each wizard component, so every dialog instance gets its own. A root
 * singleton would let two wizards opened one after the other share resume points.
 *
 * <h2>Deliberately NOT a copy of the address/bank wizard state service</h2>
 * The repo already holds seven per-feature wizard state services built around a `BehaviorSubject`
 * step STATUS whose step 1 writes immediately. That shape has no re-entry handling and guarantees
 * orphans, so it is not the model here. The mechanics implemented below follow the later pattern of
 * `ci-customer-insurable-object-create.component.ts` and `cr-object-contacts.component.ts`:
 * `ensureX()` resume points, a single-flight lock raised before the sequence, and nothing written
 * until the save sequence runs.
 *
 * German UI text with real umlauts, English code and comments (module convention).
 */
@Injectable()
export class CpProvisionContractWizardStateService {

    /** Entity alias of the Provisionszeile — route-only registration, generic CRUD service. */
    static readonly ALIAS_PROVISION_CONTRACT: string = 'cp.ProvisionContract';

    /** Entity alias of the Provisionssatz — route-only registration, generic CRUD service. */
    static readonly ALIAS_PROVISION_CONTRACT_AGENT: string = 'cp.ProvisionContractAgent';

    /**
     * What a DEFERRED field without a value says.
     *
     * Re-exported from {@link CP_PROVISION_PENDING_FIELD_TEXT} rather than spelled out here: the detail views
     * render the identical sentence, and the acceptance test asserts the wording — sharing the
     * constant makes the compiler guarantee the match instead of two authors' care. The file header
     * of {@code cp-provision-pending-text.ts} carries the reasoning, in particular why the field text
     * must not contain a time reference.
     *
     * <p>It must never appear next to a CAPTURED field — saying it over a value the user just typed
     * would call their own input provisional.
     */
    static readonly DEFERRED_HINT: string = CP_PROVISION_PENDING_FIELD_TEXT;

    /**
     * The explanation, stated ONCE at the head of a deferred block rather than per field.
     *
     * At the head it reads as what it is — a statement about the normal course of things. Repeated on
     * every field it would read as a guarantee for each of them, which two of the three states behind
     * an empty field break. Shared with the detail views via {@link CP_PROVISION_PENDING_BLOCK_TEXT}.
     */
    static readonly DEFERRED_EXPLANATION: string = CP_PROVISION_PENDING_BLOCK_TEXT;

    /**
     * The label of a CAPTURED field.
     *
     * Deliberately a plain statement of provenance and NOT a durability promise. The "a manual value
     * is never overwritten" guarantee rests on {@code source = MANUAL}, which exists on
     * {@code ProvisionContractAgent} and NOT on {@code ProvisionContract} — so a promise worded here
     * would hold for the five liability fields and quietly overreach for {@code eligibility}. What
     * is true of all six is that the user stated them, so that is what the panel says.
     */
    static readonly CAPTURED_HINT: string = 'von Ihnen erfasst';

    /** What a captured field shows before anything was typed — never blank, never the deferred hint. */
    static readonly CAPTURED_MISSING_HINT: string = 'noch nicht erfasst';

    /**
     * What a field of an ALREADY EXISTING Provisionszeile is labelled with.
     *
     * The create wizard can run against a parent that was handed in rather than created here (see
     * {@link existingContract}). A value on such a row was not captured in this run and was not
     * necessarily computed by a run either — it simply stands. Saying "von Ihnen erfasst" over it
     * would credit the wrong person, and the pending sentence over a figure that is already there would
     * be plainly false. This states only what is certain: where it came from.
     */
    static readonly EXISTING_HINT: string = 'aus der bestehenden Zeile';

    /**
     * The six fields the next ProvisionRun genuinely fills in — all on the Provisionszeile, all
     * nullable, none of them ever asked.
     *
     * Nothing is computed when a row is created by hand (signed design), and these four have no
     * NOT NULL obligation forcing them into the capture form, so the wizard leaves them out of the
     * form entirely and SAYS what will happen to them instead of presenting an empty value that
     * reads as a defect.
     *
     * <p>This list used to hold six entries. Two moved to {@link #CAPTURED_CONTRACT_FIELDS} and
     * {@link #CAPTURED_CONTRACT_AGENT_FIELDS} when it turned out that their columns are NOT NULL
     * without a default, i.e. the row cannot exist without them: calling them "deferred" was a
     * statement the code could not keep. The wizard asks for them; nothing is guessed.
     *
     * Keys are DTO attribute names of {@link ProvisionContractDto}, so a captured value can be read
     * off the form value under the very same key.
     */
    static readonly DEFERRED_CONTRACT_FIELDS: CpWizardField[] = [
        {key: 'provisionBaseDtoId', label: 'Provisionsbasis'},
        {key: 'appliedBaseCalculationMode', label: 'Angewandter Basismodus'},
        {key: 'baseReferenceAmountMonthly', label: 'Referenz-Basisbetrag monatlich'},
        {key: 'baseAmountMonthly', label: 'Basisbetrag monatlich'},
        {key: 'baseAmountTotal', label: 'Basisbetrag gesamt'},
        {key: 'agentsResolved', label: 'Provisionssätze ermittelt'}
    ];

    /**
     * The ten fields of the Provisionssatz the next ProvisionRun fills in.
     *
     * <p>Selected by the PENDING RULE, not by judgement: a field is pending exactly when it is
     * nullable without a default AND its only writer is the run. If any non-run path writes it —
     * one of the action dialogs, the Bestandsübernahme, this wizard itself — then an empty value is
     * a lasting statement in its own right and not a "not computed yet". Every one of the ten below
     * is written solely by {@code ProvisionCalculationService}
     * ({@code :2344, :2346, :2349, :2354, :2358, :2363, :2365, :2367, :2369, :2371}); no other writer
     * exists in the cp package.
     *
     * <p>The rule is deliberately a rule rather than a curated list: this set was got wrong twice
     * while it was maintained by hand, once too small and once too large. A field is added or removed
     * by re-applying the two conditions, never by an opinion about whether a caseworker would miss
     * it.
     *
     * <p>The list is what puts a pending statement back on the SATZ level at all. After the NOT NULL
     * fields moved to {@link #CAPTURED_CONTRACT_AGENT_FIELDS}, the deferred set held nothing but
     * Provisionszeile fields, so a manually created Provisionssatz made no pending statement
     * anywhere — while the obligation was explicitly to make it on BOTH levels. {@code provisionAmount}
     * matters most of the ten: it is the figure a Provisionssatz is opened for.
     *
     * <p>Consequence for the capture forms: none of the ten is ever asked. A field the run owns
     * outright must not be offered as an input, or the wizard would invite a value the next run
     * silently replaces.
     *
     * Keys are DTO attribute names of {@link ProvisionContractAgentDto}.
     */
    static readonly DEFERRED_CONTRACT_AGENT_FIELDS: CpWizardField[] = [
        {key: 'liabilityTypeAssignmentDtoId', label: 'Haftungsart (Zuordnung)'},
        {key: 'liabilityTypeDtoId', label: 'Haftungsart'},
        {key: 'appliedLiabilitySource', label: 'Herkunft Haftungsart'},
        {key: 'provisionTypeMatrixDtoId', label: 'Provisionsmatrix'},
        {key: 'appliedCalcMode', label: 'Angewandter Modus'},
        {key: 'appliedFactor', label: 'Angewandter Faktor'},
        {key: 'appliedReservePercentage', label: 'Angewandter Rücklagensatz'},
        {key: 'provisionAmount', label: 'Provisionsbetrag'},
        {key: 'reserveTargetAmount', label: 'Soll Rücklagenanteil'},
        {key: 'provisionTargetAmount', label: 'Soll Provisionsanteil'}
    ];

    /**
     * The field of the Provisionszeile the USER states.
     *
     * {@code eligibility} is {@code @NotNull} and {@code nullable = false} with no default
     * ({@code ProvisionContract.java:313-330}), so the row cannot be written without it. It is
     * asked, never guessed, and the closing step shows the value the user gave — never the deferred
     * hint.
     */
    static readonly CAPTURED_CONTRACT_FIELDS: CpWizardField[] = [
        {key: 'eligibility', label: 'Relevanz'}
    ];

    /**
     * The five liability fields of the Provisionssatz the USER states.
     *
     * All five are {@code nullable = false} with no default
     * ({@code ProvisionContractAgent.java:677-832}), so the row cannot be written without them, and
     * inventing a liability frame here would create a row the engine then disagrees with. They are
     * asked, and the value the user gave is what the closing step shows.
     *
     * Keys are DTO attribute names of {@link ProvisionContractAgentDto}.
     */
    static readonly CAPTURED_CONTRACT_AGENT_FIELDS: CpWizardField[] = [
        {key: 'liabilityMonths', label: 'Haftungsmonate'},
        {key: 'liabilityStartDate', label: 'Haftungsbeginn'},
        {key: 'liabilityEndDate', label: 'Haftungsende'},
        {key: 'inLiabilityAmount', label: 'Betrag in Haftung'},
        {key: 'liabilityStatus', label: 'Haftungsstatus'}
    ];

    /**
     * The one {@code eligibility} value that lets the run pick a hand-made Provisionszeile up.
     *
     * See {@link isOutsideRunSelection} for what hangs on it.
     */
    static readonly ELIGIBILITY_ELIGIBLE: ProvisionEligibilityResult = ProvisionEligibilityResult.ELIGIBLE;

    /**
     * Would the run pass this Provisionszeile by?
     *
     * A hand-made row only ever reaches the calculation if the selection catches it.
     * {@code ProvisionContractSelectionService} ORs four branches (:105-139); the one written for
     * exactly this case is branch 4 (:133-139), and it fires on either of two conditions:
     *
     * <pre>
     *   pc.eligibility_overwrite IS NOT NULL
     *   OR (pc.agents_resolved = FALSE AND pc.eligibility = ELIGIBLE)
     * </pre>
     *
     * {@code agentsResolved} defaults to FALSE, so the second condition comes down to
     * {@code eligibility}, which the USER states. Pick anything but ELIGIBLE and branch 4 goes
     * silent — unless an {@code eligibilityOverwrite} is set, which satisfies the branch on its own.
     * BOTH have to miss before the row is at risk, which is why this is not a plain
     * "eligibility != ELIGIBLE" test: warning on a row that carries an overwrite would be a false
     * alarm.
     *
     * <p>WHAT THIS DELIBERATELY DOES NOT CLAIM: that the row will definitely be skipped. Branch 1
     * (contract start on or after go-live) and branch 3 (an entitlement still at risk) can catch the
     * contract for entirely different reasons, and neither is knowable here without querying — which
     * would be the pre-flight this wizard must not build. The warning therefore says the row MAY go
     * unselected, and it says why. It never blocks: the choice is the user's, they only have to know
     * what it costs.
     *
     * <p>A value that has not been captured yet counts as ELIGIBLE, because that is what the create
     * wizard prefills — no warning before the user has decided anything.
     */
    isOutsideRunSelection(value: DtoDetail): boolean {

        if (!value) {
            return false;
        }

        const overwrite = value['eligibilityOverwrite'];

        if (overwrite !== null && overwrite !== undefined && overwrite !== '') {
            // Branch 4 fires on the overwrite alone — eligibility does not matter then.
            return false;
        }

        const eligibility = value['eligibility'];

        if (eligibility === null || eligibility === undefined || eligibility === '') {
            return false;
        }

        return Number(eligibility) !== CpProvisionContractWizardStateService.ELIGIBILITY_ELIGIBLE;
    }

    /**
     * The error code the backend rejects a taken time slice with.
     *
     * Used ONLY to recognise the server's answer and add a sentence explaining what to change — the
     * server's own text is always shown unchanged next to it. The wizard never runs a pre-flight
     * query and never re-implements the rule: the refusal is the backend's, and it stays there.
     *
     * <p>This replaced a match on a FRAGMENT OF THE GERMAN SENTENCE. That worked — measured — but it
     * tied the hint to the wording: reword the backend message and the extra sentence disappears in
     * silence, while the refusal itself keeps showing, so nobody notices the loss. The code survives
     * any rewording.
     *
     * <p>Set at `ProvisionContractBusinessChecks:133` via `rejectValue` on the `startDate` field.
     * It therefore only ever appears on the ARRAY answer shape; the `{data, …}` shape carries no
     * codes at all, which is why {@link isTimeSliceConflict} does not look for one there.
     */
    static readonly TIME_SLICE_CONFLICT_CODE: string = 'cp.provisionContract.timeSliceConflict';

    // ---------------------------------------------------------------------------------------- //
    // Step model
    // ---------------------------------------------------------------------------------------- //

    /** The active steps of the current run, in order. Owned by the hosting wizard. */
    steps: string[] = [];

    /** Index into {@link steps}. */
    activeStepIndex: number = 0;

    // ---------------------------------------------------------------------------------------- //
    // Drafts — everything the user captured, NOTHING of it written yet (GK13)
    // ---------------------------------------------------------------------------------------- //

    /** Captured values of the Provisionszeile form; null while the form has produced nothing. */
    contractDraft: DtoDetail = null;

    /** Whether the Provisionszeile form considers itself submittable. */
    contractSubmittable: boolean = false;

    /** The captured Provisionssätze, in capture order. Empty until the user adds the first one. */
    agentDrafts: CpProvisionContractAgentDraft[] = [];

    /** Captured values of the Provisionssatz form currently being filled in. */
    agentDraft: DtoDetail = null;

    /** Whether the Provisionssatz form considers itself submittable. */
    agentSubmittable: boolean = false;

    /** Import object context handed to both embedded forms; null = none. */
    importObjectContext: DtoImportObjectContext = null;

    // ---------------------------------------------------------------------------------------- //
    // Save sequence — lock, resume points, error
    // ---------------------------------------------------------------------------------------- //

    /**
     * SINGLE FLIGHT lock of the whole save sequence.
     *
     * Raised in {@link runContractFirst} BEFORE the first request leaves, so a second click during
     * the roundtrip finds it already true. Raising it in the response handler instead — the shape of
     * the older wizards — leaves exactly the window a double submit needs.
     */
    saveBusy: boolean = false;

    /** German message of the failed write; null while nothing failed. */
    saveError: string = null;

    /** Extra sentence naming what to change; only set for a refusal the wizard can explain. */
    saveHint: string = null;

    /** Which write failed — used to word the message and to mark the failing draft. */
    saveStep: CpProvisionWizardSaveStep = null;

    /**
     * RESUME POINT of the parent write.
     *
     * Set as soon as the Provisionszeile exists. Every later run of the sequence returns it instead
     * of writing a second row — this is what makes "Erneut versuchen" after a failed child write
     * safe.
     */
    createdContract: ProvisionContractDto = null;

    /**
     * Whether {@link createdContract} was handed in from OUTSIDE rather than written by this run.
     *
     * Set by {@link adoptExistingContract}. It changes two things and nothing else:
     * {@link ensureContractCreated} returns the row instead of writing one — an externally supplied
     * parent is the same situation as a parent reused from a failed attempt, which is why it needs no
     * second code path — and {@link isLocked} does NOT treat it as "this run has already written
     * something", because it has not. Without that second half, a wizard opened on an existing
     * Provisionszeile would start out locked and the user could never step back to fix a draft.
     */
    existingContract: boolean = false;

    /** RESUME POINT of the parent UPDATE path (adjust wizard) — see {@link ensureContractUpdated}. */
    contractUpdated: boolean = false;

    /** RESUME POINT of the child UPDATE path (adjust wizard) — see {@link ensureAgentUpdated}. */
    agentUpdated: boolean = false;

    /**
     * The Provisionssatz row as it stands BEFORE this run changes it.
     *
     * Read for one purpose: the closing step has to describe the ten run-owned fields, and those are
     * excluded from the form, so the capture draft never carries them. Describing them against the
     * draft would report every one of them as still pending on a row where the run delivered them
     * long ago — the same mistake, on the child, that reading the draft made on the parent.
     */
    existingAgent: ProvisionContractAgentDto = null;

    /** The Provisionssatz the adjust wizard wrote; kept so a retry does not repeat the write. */
    updatedAgent: ProvisionContractAgentDto = null;

    /** Key of the draft whose write failed, so the list can mark exactly that row. */
    failedDraftKey: number = null;

    /** Monotonic key generator of the drafts — never an array index, which shifts on removal. */
    private nextDraftKey: number = 1;

    constructor(protected coreService: MvsCoreService) {
    }

    // ---------------------------------------------------------------------------------------- //
    // Lifecycle
    // ---------------------------------------------------------------------------------------- //

    /**
     * Return the container to its starting state.
     *
     * Called on every (re-)open of a wizard. It clears the resume points as well: a NEW run must
     * not inherit the parent of the previous one, which is a different Provisionszeile.
     */
    reset(): void {

        this.activeStepIndex = 0;

        this.contractDraft = null;
        this.contractSubmittable = false;

        this.agentDrafts = [];
        this.agentDraft = null;
        this.agentSubmittable = false;

        this.saveBusy = false;
        this.saveError = null;
        this.saveHint = null;
        this.saveStep = null;

        this.createdContract = null;
        this.existingContract = false;
        this.contractUpdated = false;
        this.agentUpdated = false;
        this.updatedAgent = null;
        this.existingAgent = null;
        this.failedDraftKey = null;

        this.nextDraftKey = 1;
    }

    /**
     * Whether anything has already been written in this run.
     *
     * A locked run may no longer step backwards into the capture steps — the captured values are
     * what the written rows were built from, and editing them afterwards would describe something
     * that no longer matches what exists.
     */
    isLocked(): boolean {

        // An externally supplied parent is NOT a write of this run — see existingContract.
        const contractWritten = this.createdContract != null && !this.existingContract;

        return contractWritten || this.contractUpdated || this.agentUpdated
            || this.agentDrafts.some((draft: CpProvisionContractAgentDraft) => draft.created);
    }

    /** Whether a previous run left a partial result behind that a retry will reuse. */
    hasPartialResult(): boolean {
        return this.isLocked();
    }

    // ---------------------------------------------------------------------------------------- //
    // Step navigation
    // ---------------------------------------------------------------------------------------- //

    getActiveStep(): string {
        return this.steps[this.activeStepIndex];
    }

    isStepActive(step: string): boolean {
        return this.getActiveStep() === step;
    }

    isLastStep(): boolean {
        return this.activeStepIndex === this.steps.length - 1;
    }

    /** 1-based position of a step, for the numbered circle of the step card. */
    getStepNumber(step: string): number {
        return this.steps.indexOf(step) + 1;
    }

    /** Whether the flow already moved past a step, i.e. the step renders as a summary. */
    isStepDecided(step: string): boolean {

        const index = this.steps.indexOf(step);

        return index >= 0 && index < this.activeStepIndex;
    }

    goToStep(index: number): void {

        if (index < 0 || index >= this.steps.length) {
            return;
        }

        this.activeStepIndex = index;
        this.saveError = null;
        this.saveHint = null;
    }

    // ---------------------------------------------------------------------------------------- //
    // Drafts
    // ---------------------------------------------------------------------------------------- //

    /**
     * Take the values the Provisionssatz form currently holds over into the draft list.
     *
     * NOTHING is written (GK13) — the draft is a plain value object and only becomes a row inside
     * {@link ensureAgents}.
     */
    addAgentDraft(value: DtoDetail): CpProvisionContractAgentDraft {

        const draft = new CpProvisionContractAgentDraft(this.nextDraftKey++, this.copyValue(value));

        this.agentDrafts = [...this.agentDrafts, draft];

        this.agentDraft = null;
        this.agentSubmittable = false;

        return draft;
    }

    /**
     * Drop a draft again.
     *
     * A draft that has ALREADY been written is kept: removing it from the list would only hide a
     * row that exists, and this wizard has no delete path.
     */
    removeAgentDraft(key: number): void {

        this.agentDrafts = this.agentDrafts.filter(
            (draft: CpProvisionContractAgentDraft) => draft.key !== key || draft.created);
    }

    getAgentDraft(key: number): CpProvisionContractAgentDraft {
        return this.agentDrafts.find((draft: CpProvisionContractAgentDraft) => draft.key === key);
    }

    // ---------------------------------------------------------------------------------------- //
    // Write sequence
    // ---------------------------------------------------------------------------------------- //

    /**
     * Raise the single-flight lock and clear the message of the previous attempt.
     *
     * Deliberately a separate call rather than something the ensure methods do themselves: the lock
     * belongs to the WHOLE sequence, and the ensure methods run once per write.
     *
     * @return false when a sequence is already in flight — the caller must not start a second one
     */
    beginSaveSequence(): boolean {

        if (this.saveBusy) {
            return false;
        }

        this.saveBusy = true;
        this.saveError = null;
        this.saveHint = null;
        this.failedDraftKey = null;

        return true;
    }

    /** Lower the lock after the sequence ended, successfully or not. */
    endSaveSequence(): void {
        this.saveBusy = false;
    }

    /**
     * The display label of a referenced row, fetched once for a value the user picked.
     *
     * <h2>Why a fetch, and not the form</h2>
     * The obvious sources both fail for an ENTITY REFERENCE, and both were tried:
     *
     * <ul>
     *   <li>{@code <field>DtoName} in the form value is a snapshot from form generation. It names the
     *       PREFILLED row and does not follow the user's selection — that is what once made the
     *       summary show one Berater while saving another.</li>
     *   <li>The field's {@code valueList} is EMPTY for a reference field. Measured on the live form
     *       template: {@code provisionAgentDtoId} arrives with {@code access: "update"} but
     *       {@code valueList: null} and zero entries — the picker fetches its own options at render
     *       time and the template never carries them. Resolving against the option list therefore
     *       silently found nothing, which is why the summary went from a wrong name to no name.</li>
     * </ul>
     *
     * So the row is fetched. Deliberately ONCE per decision (when a draft is taken over, when a step
     * is left) and never from a template getter — a lookup driven by change detection would fire a
     * request per render.
     *
     * <h2>Which field is the label</h2>
     * There is no generic answer in the framework: {@code MvsCrudService.getObjectLabels()} returns
     * null for the route-only aliases used here. The chain below covers the DTO conventions in order
     * of specificity and ends at "#<id>", so an unlabelled entity degrades to something identifying
     * rather than to nothing. Measured example: {@code cp.ProvisionAgent} carries neither
     * {@code name} nor {@code formatName} — its label is {@code agentDtoName}, which the
     * {@code *DtoName} step finds.
     *
     * @param alias entity alias of the referenced row
     * @param id    the id the user selected
     */
    resolveReferenceLabel(alias: string, id: any): Observable<string> {

        if (id == null || id === '') {
            return of(null);
        }

        const service = this.resolveService(alias);

        if (!service) {
            return of(null);
        }

        return service.get(Number(id)).pipe(
            map((dto: DtoDetail) => this.pickLabel(dto, id))
        );
    }

    /** The display text of a fetched row — see {@link resolveReferenceLabel} for the order. */
    protected pickLabel(dto: DtoDetail, id: any): string {

        if (!dto) {
            return null;
        }

        if (dto['formatName']) {
            return String(dto['formatName']);
        }

        if (dto['name']) {
            return String(dto['name']);
        }

        // Sorted so the choice is deterministic when an entity carries more than one.
        const nameKey = Object.keys(dto)
            .filter((key: string) => key.endsWith('DtoName') && !!dto[key])
            .sort()[0];

        return nameKey ? String(dto[nameKey]) : '#' + id;
    }

    /**
     * Load an EXISTING Provisionszeile and make it the parent of this run.
     *
     * The read is the wizard's own preparation, not a business pre-check: it fetches the row the
     * caller named so the wizard can show it and so the three denormalised keys can be derived from
     * it. It judges nothing and refuses nothing — every rule stays on the server.
     *
     * @param provisionContractId the row handed in by the caller
     */
    loadExistingContract(provisionContractId: number): Observable<ProvisionContractDto> {

        const service = this.resolveService(CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT);

        if (!service) {
            return throwError(() => new Error(
                'Service for ' + CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT
                + ' is not registered.'));
        }

        return service.get(provisionContractId).pipe(
            tap((loaded: DtoDetail) => this.adoptExistingContract(loaded as ProvisionContractDto)),
            map((loaded: DtoDetail) => loaded as ProvisionContractDto)
        );
    }

    /**
     * Adopt a Provisionszeile that already exists as the parent of this run.
     *
     * It is placed on the very field a written parent would occupy, so the whole save sequence keeps
     * working unchanged: {@link ensureContractCreated} finds a parent and returns it, exactly as it
     * does for a parent left behind by a failed attempt. {@link existingContract} is what keeps the
     * two apart everywhere the difference matters.
     */
    adoptExistingContract(contract: ProvisionContractDto): void {

        this.createdContract = contract;
        this.existingContract = contract != null;
    }

    /**
     * Whether the adopted parent carries the three keys a child row is judged against.
     *
     * {@code ProvisionContractAgentOnChange.assertKeysDerivedFromProvisionContract} refuses a child
     * whose {@code contract}, {@code provisionType} and {@code provisionTypeContractEvent} disagree
     * with its parent, and treats a missing value as a disagreement. The wizard copies all three off
     * the parent, so if the parent it loaded does not carry them, every child write of this run would
     * be refused with a message about a rule the user did not break.
     *
     * This is a check on THIS COMPONENT'S OWN LOAD, not a pre-flight check of a business rule: it
     * asks "did my read give me what I need", never "would the server accept this".
     */
    hasDerivableKeys(): boolean {

        const parent = this.createdContract;

        return !!parent
            && parent.contractDtoId != null
            && parent.provisionTypeDtoId != null
            && parent.provisionTypeContractEventDtoId != null;
    }

    /**
     * Load the Provisionssatz as it stands, so the closing step can describe its run-owned fields.
     *
     * Like {@link loadExistingContract} this is the wizard's own preparation, not a business check:
     * it reads what is there in order to display it truthfully, and judges nothing.
     */
    loadExistingAgent(provisionContractAgentId: number): Observable<ProvisionContractAgentDto> {

        const service = this.resolveService(
            CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT_AGENT);

        if (!service) {
            return throwError(() => new Error(
                'Service for ' + CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT_AGENT
                + ' is not registered.'));
        }

        return service.get(provisionContractAgentId).pipe(
            tap((loaded: DtoDetail) => this.existingAgent = loaded as ProvisionContractAgentDto),
            map((loaded: DtoDetail) => loaded as ProvisionContractAgentDto)
        );
    }

    /**
     * RESUME POINT 1 (create path) — the Provisionszeile.
     *
     * Returns the row an earlier, failed run already created instead of writing a second one. That
     * is the whole point: the parent carries a unique business key (Vertrag, Provisionsart,
     * Ereignisdatum), so a second write would not simply duplicate it, it would be REFUSED and the
     * user could never finish a run that failed on a child.
     *
     * <p>The SAME early return carries the existing-parent mode: a Provisionszeile handed in by the
     * caller is adopted onto {@link createdContract} up front, so this method finds a parent and
     * writes nothing. An externally supplied parent and a parent reused after a failure are the same
     * situation — one row that already exists and must not be created a second time — and they
     * deliberately share one code path.
     *
     * @param payload the create payload assembled by the wizard
     */
    ensureContractCreated(payload: DtoDetail): Observable<ProvisionContractDto> {

        if (this.createdContract) {
            return of(this.createdContract);
        }

        this.saveStep = 'contract';

        const service = this.resolveService(CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT);

        if (!service) {
            return throwError(() => new Error(
                'Service for ' + CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT
                + ' is not registered.'));
        }

        return service.create(payload, this.importObjectContext).pipe(
            tap((created: DtoDetail) => this.createdContract = created as ProvisionContractDto),
            map((created: DtoDetail) => created as ProvisionContractDto)
        );
    }

    /**
     * RESUME POINT 1 (adjust path) — the Provisionszeile update.
     *
     * An update is repeatable in principle, but repeating it after a failure further down the
     * sequence would send the captured snapshot a SECOND time — and by then the row may already
     * carry values the next step wrote. The flag keeps the retry to exactly what still has to
     * happen.
     *
     * @param payload the update payload, id included
     */
    ensureContractUpdated(payload: DtoDetail): Observable<ProvisionContractDto> {

        if (this.contractUpdated) {
            return of(this.createdContract);
        }

        this.saveStep = 'contract';

        const service = this.resolveService(CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT);

        if (!service) {
            return throwError(() => new Error(
                'Service for ' + CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT
                + ' is not registered.'));
        }

        return service.update(payload, this.importObjectContext).pipe(
            tap((updated: DtoDetail) => {
                this.createdContract = updated as ProvisionContractDto;
                this.contractUpdated = true;
            }),
            map((updated: DtoDetail) => updated as ProvisionContractDto)
        );
    }

    /**
     * RESUME POINT 2 (create path) — the 1..N Provisionssätze, written one after the other.
     *
     * `concatMap` and not `mergeMap`: the rows share the business key (Provisionszeile,
     * Provisionsart, Abrechnungskonto), so writing them in parallel would let two drafts with the
     * same triple both pass the uniqueness check before either had been persisted. Sequential
     * writing also makes the failure point unambiguous — everything before the failing draft is
     * written, everything from it on is not, and the retry continues exactly there.
     *
     * @param parent the Provisionszeile the rows hang on
     */
    ensureAgents(parent: ProvisionContractDto): Observable<ProvisionContractAgentDto[]> {

        if (!this.agentDrafts.length) {
            return of([]);
        }

        return from(this.agentDrafts).pipe(
            concatMap((draft: CpProvisionContractAgentDraft) => this.ensureAgent(draft, parent)),
            toArray()
        );
    }

    /**
     * ONE Provisionssatz of the create path, guarded by its own resume point.
     *
     * @param draft  the captured values
     * @param parent the Provisionszeile the row hangs on
     */
    ensureAgent(
        draft: CpProvisionContractAgentDraft, parent: ProvisionContractDto): Observable<ProvisionContractAgentDto> {

        if (draft.createdDto) {
            return of(draft.createdDto);
        }

        this.saveStep = 'agent';
        this.failedDraftKey = draft.key;

        const service = this.resolveService(
            CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT_AGENT);

        if (!service) {
            return throwError(() => new Error(
                'Service for ' + CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT_AGENT
                + ' is not registered.'));
        }

        return service.create(this.buildAgentPayload(draft.value, parent), this.importObjectContext).pipe(
            tap((created: DtoDetail) => {
                draft.createdDto = created as ProvisionContractAgentDto;
                draft.createdId = created?.id;
                this.failedDraftKey = null;
            }),
            map((created: DtoDetail) => created as ProvisionContractAgentDto)
        );
    }

    /**
     * RESUME POINT 2 (adjust path) — the Provisionssatz update.
     *
     * @param payload the update payload, id included
     */
    ensureAgentUpdated(payload: DtoDetail): Observable<ProvisionContractAgentDto> {

        if (this.agentUpdated) {
            return of(this.updatedAgent);
        }

        this.saveStep = 'agent';

        const service = this.resolveService(
            CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT_AGENT);

        if (!service) {
            return throwError(() => new Error(
                'Service for ' + CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT_AGENT
                + ' is not registered.'));
        }

        return service.update(payload, this.importObjectContext).pipe(
            tap((updated: DtoDetail) => {
                this.updatedAgent = updated as ProvisionContractAgentDto;
                this.agentUpdated = true;
            }),
            map((updated: DtoDetail) => updated as ProvisionContractAgentDto)
        );
    }

    /**
     * The create payload of ONE Provisionssatz.
     *
     * Two groups of keys are added on top of what the form captured, and BOTH are derivations, never
     * inventions:
     *
     *   * `provisionContractDtoId` — the parent this run just created. The field is not offered in
     *     the form at all: at capture time the Provisionszeile does not exist yet.
     *   * `contractDtoId`, `provisionTypeDtoId`, `provisionTypeContractEventDtoId` — the three
     *     denormalised keys. `ProvisionContractAgentOnChange.assertKeysDerivedFromProvisionContract`
     *     REFUSES a row whose copies disagree with the parent, and it does NOT fill them in on the
     *     generic CRUD path. Copying them off the parent response is therefore mandatory, and it is
     *     the exact value the guard compares against — the wizard decides nothing here.
     *
     * `source` is deliberately NOT set: `ProvisionContractAgentOnChange.forceManualSource` stamps
     * MANUAL on every row that reaches the generic stack. Sending it from here would be a second
     * place holding the same guarantee.
     */
    buildAgentPayload(value: DtoDetail, parent: ProvisionContractDto): DtoDetail {

        const payload = this.copyValue(value);

        payload['provisionContractDtoId'] = parent?.id;

        payload['contractDtoId'] = parent?.contractDtoId;
        payload['provisionTypeDtoId'] = parent?.provisionTypeDtoId;
        payload['provisionTypeContractEventDtoId'] = parent?.provisionTypeContractEventDtoId;

        return payload;
    }

    /** Shallow copy of a captured form value into a plain DtoDetail. */
    copyValue(value: DtoDetail): DtoDetail {

        const copy = new DtoDetail();

        for (const key of Object.keys(value || {})) {
            copy[key] = value[key];
        }

        return copy;
    }

    // ---------------------------------------------------------------------------------------- //
    // Error surfacing — the backend refusals, shown rather than re-implemented
    // ---------------------------------------------------------------------------------------- //

    /**
     * Turn a failed write into the two lines the wizard renders.
     *
     * The SERVER's own text always wins: {@code CheckErrors} answers with the field messages the
     * `*OnChange` guards produced, and those already name the rule in German. Everything this method
     * adds is context — which of the two writes failed, and for the one refusal the wizard can
     * explain, a sentence saying what to change.
     *
     * Explicitly NOT done here: judging whether the write WOULD be refused. The time-slice rule
     * lives in `ProvisionContractOnChange.assertUniqueEventKey` and is enforced there; a pre-flight
     * copy in the frontend would be a second implementation that starts drifting the day the
     * backend rule changes.
     */
    resolveErrorMessage(error: any): void {

        const serverMessage = this.extractServerMessage(error);
        const suffix = serverMessage ? ' ' + serverMessage : '';

        if (this.saveStep === 'derive') {
            this.saveError = 'Die Provisionszeile steht, führt aber weder Vertrag noch Provisionsart '
                + 'und Ereignis mit. Ohne diese drei Angaben lässt sich kein Provisionssatz zu ihr '
                + 'anlegen.';
        } else if (this.saveStep === 'contract') {
            this.saveError = 'Die Provisionszeile konnte nicht gespeichert werden.' + suffix;
        } else if (this.saveStep === 'agent') {
            this.saveError = 'Der Provisionssatz konnte nicht gespeichert werden.' + suffix;
        } else {
            this.saveError = 'Der Vorgang konnte nicht abgeschlossen werden.' + suffix;
        }

        this.saveHint = this.isTimeSliceConflict(error)
            ? 'Für diesen Vertrag und diese Provisionsart besteht zu diesem Ereignisdatum bereits eine '
            + 'Zeitscheibe. Wählen Sie ein anderes Ereignisdatum oder bearbeiten Sie die bestehende '
            + 'Provisionszeile mit der manuellen Anpassung.'
            : null;
    }

    /**
     * Whether the server answered with the time-slice refusal of the Provisionszeile.
     *
     * Reads the RAW error rather than the assembled message, because the code lives on the entry and
     * not in the text. Only the array shape carries codes — a refusal delivered as `{data, …}` has
     * none, so it is answered with false rather than searched for something that cannot be there.
     */
    isTimeSliceConflict(error: any): boolean {

        const details = error?.error;

        if (!Array.isArray(details)) {
            return false;
        }

        return details.some((detail: any) =>
            detail?.code === CpProvisionContractWizardStateService.TIME_SLICE_CONFLICT_CODE);
    }

    /**
     * The field messages of a rejected write, joined into one line.
     *
     * A refused write answers with a Spring error list — that list is the only part of the response
     * that says what is actually wrong. A plain error object with a `message` is accepted as well, so
     * a technical failure is not swallowed into silence.
     *
     * <h2>Why the list is filtered at all</h2>
     * One rejected field can produce TWO entries: the bean-validation constraint that fired, and a
     * business check explaining the same problem in German. Both are shown, in registration order, so
     * the user reads "must not be null Der gewählte Vertrag trägt weder …" — an untranslated fragment
     * in front of the sentence that actually helps. The order cannot be fixed on the server: the
     * bean validation registers first by construction.
     *
     * <h2>The rule, and what makes it safe</h2>
     * A constraint entry is dropped ONLY when another entry explains the SAME field. Concretely:
     * an entry produced by the bean validation carries `arguments`, one produced by a business check
     * does not — see {@link isBeanValidationEntry}. That difference comes from the two call paths and
     * needs no list of names to be kept up to date.
     *
     * <p>Two properties matter more than the filtering itself:
     * <ul>
     *   <li>Nothing is dropped without a REPLACEMENT for the same field. A lone `NotNull` survives —
     *       the user still has to learn that a value is missing.</li>
     *   <li>If the filter would empty the list, the UNFILTERED list is used. The worst case is
     *       therefore today's behaviour, never a silent swallow. A filter that loses a real error
     *       message would be far worse than an ugly fragment.</li>
     * </ul>
     *
     * <p>An unknown code counts as an explanation and is KEPT — the safe direction: a custom
     * constraint annotation shows one message too many rather than none.
     *
     * <p>MEASURED, not inferred (raw response body of a create against a contract carrying neither
     * application nor start date): both entries arrive as FieldError on `applicationDate`, one with
     * `code: "NotNull"` and populated `arguments`, one with `code: "notDerivable"` and
     * `arguments: null`. An earlier version of this rule keyed on the ABSENCE of a code — derived
     * from the source, and wrong: the business check registers through `rejectValue` and carries a
     * code of its own, so that rule would have filtered nothing at all.
     *
     * <p>NOT handled here, and deliberately so: a refusal that arrives as
     * `{data, success, timestamp}` instead of a list (the business-key rejection). It never reaches
     * the array branch, and the global HTTP error interceptor already surfaces its `data` text.
     */
    extractServerMessage(error: any): string {

        const details = error?.error;

        if (Array.isArray(details)) {

            const explainedFields = new Set<string>(
                details
                    .filter((detail: any) => !!detail?.field && !this.isBeanValidationEntry(detail))
                    .map((detail: any) => String(detail.field)));

            const kept = details.filter((detail: any) =>
                !(this.isBeanValidationEntry(detail)
                    && !!detail?.field
                    && explainedFields.has(String(detail.field))));

            // Never let the filter empty the list — see the class of failure this guards against.
            const shown = (kept.length ? kept : details)
                .filter((detail: any) => !!detail?.defaultMessage);

            return shown
                .map((detail: any) => this.formatDetail(detail, shown.length > 1))
                .join(' ');
        }

        // A refusal that arrives as the Alpha envelope {data, success, timestamp} rather than as a
        // list — the business-key rejections use this shape. `data` carries the German sentence.
        if (details && typeof details['data'] === 'string') {
            return details['data'];
        }

        if (details && typeof details['message'] === 'string') {
            return details['message'];
        }

        return '';
    }

    /*
     * ── WHY THE ENVELOPE MESSAGE IS SHOWN HERE ALTHOUGH A TOAST ALREADY CARRIES IT ─────────────
     *
     * The global HttpErrorInterceptor renders `error.error.data` as a toast for every failed call
     * (the `error["error"]["data"]` branch of the catchError in
     * features/core/shared/error/http.error.intercepter.ts). Reading the same sentence into the
     * wizard's own error panel therefore shows it twice for a moment.
     *
     * That is accepted deliberately, and the alternative is worse: a toast is transient and appears
     * away from the dialog the user is working in. Whoever misses it or dismisses it is left with
     * "Der Provisionssatz konnte nicht gespeichert werden." and no reason at all — while the panel
     * sits exactly where the correction has to be made and stays until it is.
     *
     * SUPPRESSING THE TOAST IS POSSIBLE IN PRINCIPLE, BUT NOT REACHABLE FROM THIS WRITE PATH. An
     * earlier version of this note said suppression did not exist at all; that is no longer true.
     * `HttpErrorInterceptor.intercept` DOES evaluate `SUPPRESS_ERROR_HANDLER` (declared in
     * features/core/shared/service/crud/suppress-error-token.ts) via `request.context.get(...)`, and
     * a caller passing `{context: new HttpContext().set(SUPPRESS_ERROR_HANDLER, true)}` raises no
     * toast for that one request — the token defaults to false, so nothing else is affected.
     *
     * What blocks it HERE is the write path, not the interceptor: this wizard saves through
     * `MvsCrudService.create()/update()` (see `resolveService`), and that service neither takes an
     * options argument nor builds an HttpContext anywhere, so there is no seam to set the token on.
     * Plumbing one through the framework crud service is still a change to a file the whole repo
     * depends on, and still out of scope for this wizard.
     *
     * The panel keeps its message either way: suppression removes the toast, never the error, so the
     * reasoning above stands on its own merits and is not a workaround for a missing feature.
     *
     * This is also the established answer elsewhere in the repo rather than a local invention — see
     * the "WHERE THE MESSAGE BELONGS ON SCREEN" section of
     * `features/feature-crm/cw/util/cw-api-error.util.ts`, which reaches the same conclusion for the
     * same reasons and lists the cw components that follow it.
     */

    /**
     * One error entry as a line of text.
     *
     * The field is prefixed ONLY when several messages are shown at once. That is the case the prefix
     * exists for: two bare constraint messages read as "must not be null must not be null", which
     * says something failed but not what. With a single message there is nothing to tell apart, and a
     * prefix would only push a technical token in front of a sentence that already stands on its own
     * — the normal case, where a business check explains the problem in German.
     *
     * <p>THE PREFIX IS THE TECHNICAL FIELD NAME, and that is a known limitation rather than a
     * choice. Measured on the raw body: the entry carries `field: "applicationDate"`, and its
     * `arguments` carry the same token again — the response holds no readable label anywhere. The
     * readable one ("Antragsdatum", "Haftungssumme") lives in the FORM TEMPLATE as `uiLabel`, which
     * this container does not have. "applicationDate: must not be null" is still strictly more
     * usable than the same sentence twice with nothing to distinguish it.
     *
     * <p>SELECTION IS UNAFFECTED. This method decides how an entry READS, never whether it is shown —
     * the filter above owns that, including the guarantee that nothing is dropped without a
     * replacement for the same field.
     */
    protected formatDetail(detail: any, withField: boolean): string {

        const message = String(detail?.defaultMessage);

        return withField && detail?.field ? String(detail.field) + ': ' + message : message;
    }

    /**
     * Whether an error entry was produced by the BEAN VALIDATION rather than by a business check.
     *
     * The discriminator is {@code arguments}, and it comes from the two CALL PATHS themselves, not
     * from any naming convention:
     *
     * <ul>
     *   <li>A constraint violation is translated by Spring's validator adapter, which always attaches
     *       the resolvable field name as an ARGUMENT (so "must not be null" can be rendered with the
     *       field in it). Measured: {@code arguments} populated.</li>
     *   <li>A business rejection goes through {@code rejectValue(field, code, defaultMessage)}, which
     *       takes no arguments at all. Measured: {@code arguments: null}.</li>
     * </ul>
     *
     * <p>An earlier version keyed on the error CODE against a hard-coded list of JSR-380 constraint
     * names. It worked on the measured bodies but was rejected for a good reason: such a list has to
     * be maintained by hand, and the day someone adds a constraint nobody updates it — the filter
     * then silently stops working. {@code arguments} needs no list.
     *
     * <p>An entry that carries no arguments counts as an explanation and is KEPT, which is the safe
     * direction: showing one message too many beats swallowing the one that explains the problem.
     */
    protected isBeanValidationEntry(detail: any): boolean {

        const args = detail?.arguments;

        return Array.isArray(args) ? args.length > 0 : !!args;
    }

    // ---------------------------------------------------------------------------------------- //
    // Closing-step fields — the honesty duty, in its two honest halves
    // ---------------------------------------------------------------------------------------- //

    /**
     * Render one field of the closing step as "label -> what stands there".
     *
     * Never an EMPTY cell — that is the whole reason this method exists: a blank next to
     * "Haftungsbeginn" reads as a defect. What fills the gap depends on which half the field belongs
     * to, and the two must not be mixed up:
     *
     *   * DEFERRED — nothing was captured, because the field is never asked. The pending sentence
     *     stands, and it is the truth: the next run computes it.
     *   * CAPTURED — the user states the field, so the value they gave is shown. The pending
     *     sentence must NOT appear here; it would describe their input as provisional. Until they
     *     have typed anything the placeholder says exactly that, and nothing more.
     *
     * @param field    the field to describe
     * @param value    the captured form value the field is read off
     * @param deferred which half the field belongs to
     */
    describeField(
        field: CpWizardField, value: DtoDetail, deferred: boolean, valueHint?: string): CpWizardFieldState {

        const raw = value ? value[field.key] : null;
        const captured = raw !== null && raw !== undefined && raw !== '';

        return {
            key: field.key,
            label: field.label,
            deferred: deferred,
            captured: captured,
            value: captured ? raw : null,
            hint: captured
                ? (valueHint
                    ? valueHint
                    : (deferred
                        ? CpProvisionContractWizardStateService.DEFERRED_HINT
                        : CpProvisionContractWizardStateService.CAPTURED_HINT))
                : (deferred
                    ? CpProvisionContractWizardStateService.DEFERRED_HINT
                    : CpProvisionContractWizardStateService.CAPTURED_MISSING_HINT)
        };
    }

    /**
     * The six genuinely deferred fields of the Provisionszeile.
     *
     * <p>Two sources, and getting them the right way round is the whole honesty of this block:
     *
     * <ul>
     *   <li><b>New parent</b> — {@code value} is the capture draft, in which these five never appear
     *       (they are excluded from the form). Every one of them renders the pending sentence, and
     *       that is the truth: the row does not exist yet and the next run will fill them.</li>
     *   <li><b>Existing parent</b> — {@code value} is the LOADED row, which may well have been
     *       through a run already. A field that carries a figure renders that figure with
     *       {@code valueHint}; only the ones still empty keep the pending sentence. Feeding the
     *       (empty) draft here instead would claim the next run still owes five values that are
     *       long since computed.</li>
     * </ul>
     *
     * @param value     the draft of this run, or the loaded parent row
     * @param valueHint what a populated field is labelled with; omit for the new-parent case
     */
    describeDeferredContractFields(value: DtoDetail, valueHint?: string): CpWizardFieldState[] {

        return CpProvisionContractWizardStateService.DEFERRED_CONTRACT_FIELDS.map(
            (field: CpWizardField) => this.describeField(field, value, true, valueHint));
    }

    /**
     * The field of the Provisionszeile the user states.
     *
     * Same two sources as above: for an existing parent the value was stated by whoever created that
     * row, not by this run, so {@code valueHint} lets the caller say so instead of crediting the
     * current user with an entry they never made.
     */
    describeCapturedContractFields(value: DtoDetail, valueHint?: string): CpWizardFieldState[] {

        return CpProvisionContractWizardStateService.CAPTURED_CONTRACT_FIELDS.map(
            (field: CpWizardField) => this.describeField(field, value, false, valueHint));
    }

    /**
     * The ten deferred fields of the Provisionssatz.
     *
     * TWO sources, exactly as on the parent side. The create wizard passes the capture draft: none of
     * the ten is in the form and the row is brand new, so all ten correctly render the pending
     * sentence. The adjust wizard passes the row as it STANDS, because there the run may have
     * delivered them already — a populated field then shows its value under {@code valueHint}, and
     * only the ones still empty keep the pending sentence.
     */
    describeDeferredAgentFields(value: DtoDetail, valueHint?: string): CpWizardFieldState[] {

        return CpProvisionContractWizardStateService.DEFERRED_CONTRACT_AGENT_FIELDS.map(
            (field: CpWizardField) => this.describeField(field, value, true, valueHint));
    }

    /** The five liability fields of the Provisionssatz the user states. */
    describeCapturedAgentFields(value: DtoDetail): CpWizardFieldState[] {

        return CpProvisionContractWizardStateService.CAPTURED_CONTRACT_AGENT_FIELDS.map(
            (field: CpWizardField) => this.describeField(field, value, false));
    }

    // ---------------------------------------------------------------------------------------- //
    // Internals
    // ---------------------------------------------------------------------------------------- //

    /**
     * Resolve the CRUD service of an alias.
     *
     * Both aliases are registered route-only in `cp.entity-provider.ts`, so the core service hands
     * out a generic `MvsCrudService` bound to the module's REST route. That is the same write path
     * the existing action dialogs use and the same one `ProvisionContractAgentOnChange` intercepts.
     */
    protected resolveService(alias: string): MvsCrudService {
        return this.coreService.getCrudService(alias);
    }

}

/**
 * Which part of a save sequence is running — used to word the German error message.
 *
 * {@code derive} is not a write: it is the step between the two writes in which the child payload is
 * built from the parent. It has its own value because its failure message has to say that the
 * Provisionszeile DID get written — telling the user it could not be saved would send them looking
 * for something that is there.
 */
export type CpProvisionWizardSaveStep = 'contract' | 'agent' | 'derive';

/** One field of the closing step: the DTO attribute name and the German label it is shown under. */
export interface CpWizardField {
    key: string;
    label: string;
}

/** What the closing step renders for one field. Never an empty cell — see the producer. */
export interface CpWizardFieldState {
    key: string;
    label: string;
    /**
     * true = the next run fills the field in and it is never asked;
     * false = the user states it, and {@link #value} is what they gave.
     */
    deferred: boolean;
    /** true = the run captured a value for the field. */
    captured: boolean;
    /** The captured value, or null when nothing was captured. */
    value: any;
    /**
     * The German sentence shown beside the field, always populated: the pending sentence for a
     * deferred field, the provenance statement for a captured one.
     */
    hint: string;
}

/**
 * ONE captured Provisionssatz of the create wizard.
 *
 * A draft is a pure value object until {@link CpProvisionContractWizardStateService.ensureAgent}
 * writes it. {@link createdDto} is its resume point: once set, a retry of the sequence skips this
 * draft instead of writing a second row for the same triple.
 */
export class CpProvisionContractAgentDraft {

    /**
     * Display labels of the two references, resolved ONCE when the draft was taken over.
     *
     * Held on the draft rather than looked up while rendering: the lookup is a request, and a summary
     * getter runs on every change detection pass. Null until the fetch returns, which is why the
     * summary falls back to a neutral label instead of showing an empty gap.
     */
    agentLabel: string = null;
    componentLabel: string = null;

    /** The written row; null as long as the draft has not been written. */
    createdDto: ProvisionContractAgentDto = null;

    /** Id of the written row, for the list. */
    createdId: number = null;

    constructor(
        /** Stable identity of the draft — never the array index, which shifts on removal. */
        public readonly key: number,
        /** The captured form values. */
        public value: DtoDetail) {
    }

    get created(): boolean {
        return this.createdDto != null;
    }

}
